/**
 * "Później" and "Gotowe", standing alone under the card at the card's width, plus the caret that
 * opens the four terms.
 *
 * Owner, 18 September 2026: these do NOT move into the card's footer. They speak about the trainer's
 * own list; the buttons inside the card speak about the content.
 *
 * Both work on every card, including the two whose primary is refused — a response is a statement
 * about the trainer's list, not about the module the item came from (attention-queue R4).
 */

import { Menu } from '@base-ui/react/menu';
import { useTranslation } from 'react-i18next';

import { CheckIcon } from '~/shared/ui/AuthIcons';
import { ElapsedMarkIcon } from '~/shared/ui/EmptyStateIcons';
import menu from '~/shared/ui/MenuSurface.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { SnoozePreset } from '~/features/attention-queue';

import { CaretIcon } from './TaskIcons';
import styles from './decisionBar.module.css';

interface DecisionBarProps {
  /** Who the two acts are about, for each control's accessible name. */
  readonly person: string;
  /** The card already has an answer: both acts are refused rather than removed. */
  readonly answered: boolean;
  /** The four terms "Później" can name, already resolved against the trainer's clock. */
  readonly presets: readonly SnoozePreset[];
  /** Each term's instant, worded for the reader. */
  readonly termWhen: (preset: SnoozePreset) => string;
  readonly onLater: (preset: SnoozePreset) => void;
  readonly onDone: () => void;
  /** Whether the term menu is open — `⇧←` opens it, so the screen owns the flag. */
  readonly termsOpen: boolean;
  readonly onTermsOpenChange: (open: boolean) => void;
}

export function DecisionBar({
  person,
  answered,
  presets,
  termWhen,
  onLater,
  onDone,
  termsOpen,
  onTermsOpenChange,
}: DecisionBarProps) {
  const { t } = useTranslation();
  const fallback = presets.find((preset) => preset.isDefault) ?? presets[0];

  return (
    <div className={styles.bar}>
      <DecisionRow
        person={person}
        answered={answered}
        word={t('web:tasks.decide.later')}
        hint={t('web:tasks.decide.laterHint')}
        glyph={<ElapsedMarkIcon className={styles.glyphMark} />}
        shortcut="←"
        onPress={() => {
          if (fallback !== undefined) onLater(fallback);
        }}
        tail={
          /* The four terms. Base UI supplies focus, dismissal and positioning; every visible byte
           * is ours, on the surface every other menu in this app wears. */
          <Menu.Root open={termsOpen} onOpenChange={onTermsOpenChange}>
            <Menu.Trigger className={styles.terms} disabled={answered}>
              <CaretIcon className={styles.termsGlyph} />
              <span className={hidden.srOnly}>{t('web:tasks.decide.terms')}</span>
            </Menu.Trigger>
            <Menu.Portal>
              <Menu.Positioner className={menu.positioner} sideOffset={8} align="start">
                <Menu.Popup className={`${String(menu.popup)} ${String(styles.menu)}`}>
                  <Menu.Group>
                    {/* The label sits INSIDE the group. Base UI reads its group off context and
                      * THROWS — "MenuGroupContext is missing" — when a `Menu.GroupLabel` stands
                      * beside one, which took the whole menu down on every open. The builder's own
                      * `RefusableRadioMenu` carries the same note for the same reason. */}
                    <Menu.GroupLabel className={`${String(menu.groupLabel)} ${String(styles.menuLabel)}`}>
                      {t('web:tasks.decide.termsHeader')}
                    </Menu.GroupLabel>
                    {presets.map((preset) => (
                      <Menu.Item
                        className={menu.item}
                        key={preset.id}
                        onClick={() => {
                          onLater(preset);
                        }}
                      >
                        <span className={menu.itemLabel}>{t(preset.labelKey)}</span>
                        <span className={styles.termWhen}>{termWhen(preset)}</span>
                      </Menu.Item>
                    ))}
                  </Menu.Group>
                </Menu.Popup>
              </Menu.Positioner>
            </Menu.Portal>
          </Menu.Root>
        }
      />

      <DecisionRow
        person={person}
        answered={answered}
        word={t('web:tasks.decide.done')}
        hint={t('web:tasks.decide.doneHint')}
        glyph={<CheckIcon className={styles.glyphMark} />}
        glyphClassName={styles.glyphDone}
        shortcut="→"
        onPress={onDone}
      />
    </div>
  );
}

interface DecisionRowProps {
  readonly person: string;
  /** The card already has an answer: the act is refused rather than removed. */
  readonly answered: boolean;
  readonly word: string;
  /** What the act does, under the word. The DEFAULT term for "Później". */
  readonly hint: string;
  readonly glyph: React.ReactNode;
  /** The one place the accent of a completion lives — see `.glyphDone`. */
  readonly glyphClassName?: string;
  /** The key that does the same thing, printed on the row. */
  readonly shortcut: string;
  /** Anything that stands between the act and its key. The term menu, for "Później" alone. */
  readonly tail?: React.ReactNode;
  readonly onPress: () => void;
}

/**
 * One of the two acts: a button, its glyph, its word over its hint, and the key that does the same.
 *
 * The two were the same twenty lines with two words swapped, and the refusal, the accessible name
 * and the `aria-disabled` had to be got right in both. What actually differs is the word, the
 * glyph, the key — and that only "Później" has anything between them.
 */
function DecisionRow({
  person,
  answered,
  word,
  hint,
  glyph,
  glyphClassName,
  shortcut,
  tail,
  onPress,
}: DecisionRowProps) {
  const { t } = useTranslation();

  return (
    <div className={`${styles.row} ${answered ? styles.rowAnswered : ''}`}>
      <button
        className={styles.act}
        type="button"
        // `aria-disabled`, never `disabled`: a control that disables itself under the trainer's own
        // focus drops the keyboard on `<body>`. The no-op is therefore written out.
        aria-disabled={answered ? 'true' : undefined}
        aria-label={t('web:home.action.forTask', { action: word, task: person })}
        onClick={() => {
          if (!answered) onPress();
        }}
      >
        <span className={`${styles.glyph} ${glyphClassName ?? ''}`}>{glyph}</span>
        <span className={styles.words}>
          {word}
          <small className={styles.hint}>{hint}</small>
        </span>
      </button>
      <span className={styles.tail}>
        {tail}
        <kbd className={styles.key}>{shortcut}</kbd>
      </span>
    </div>
  );
}
